import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, MapPin, Clock, Shield, Users, Car, ChevronRight, Phone, Mail, Globe, Star,
  CheckCircle2, Building2, Briefcase, Heart, Coffee, Trophy, Wine
} from "lucide-react";
import PageTransition from "../../components/PageTransition";

const MelbournePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Schema markup for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me - Melbourne",
    "description": "Melbourne luxury chauffeur services for airport transfers, sporting events, corporate travel and winery tours. Premium fleet with professional chauffeurs.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Melbourne",
      "addressRegion": "VIC",
      "addressCountry": "AU"
    },
    "telephone": "+61481437772",
    "email": "info@limonearme.com.au",
    "url": "https://www.limonearme.com.au/chauffeur/melbourne",
    "priceRange": "$$-$$$",
    "openingHours": "Mo-Su 00:00-23:59",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "142"
    },
    "areaServed": {
      "@type": "City",
      "name": "Melbourne"
    }
  };

  const services = [
    { 
      icon: Plane, 
      title: "Melbourne Airport Limo Transfers", 
      desc: "Travel between Melbourne Airport (MEL) and your destination in style. Professional chauffeurs with local knowledge and superior vehicles ideal for business & leisure."
    },
    { 
      icon: Trophy, 
      title: "Sporting Events & MCG Transfers", 
      desc: "Luxury transport to MCG, Melbourne Park, Flemington Racecourse, and major sporting events including AFL, F1, and the Australian Open."
    },
    { 
      icon: Wine, 
      title: "Yarra Valley & Winery Tours", 
      desc: "Chauffeur-driven tours to Yarra Valley wineries, Mornington Peninsula, and Melbourne's famous laneways and restaurants."
    },
    { 
      icon: Briefcase, 
      title: "Corporate & Business Travel", 
      desc: "Executive chauffeur services throughout Melbourne CBD, Southbank, Docklands, and major business precincts."
    }
  ];

  const whyChoose = [
    "Professional chauffeurs with local knowledge",
    "Superior vehicles ideal for business & leisure",
    "Seamless bookings for domestic or international arrivals",
    "On-time service with flight tracking",
    "Flexible and custom-designed travel solutions",
    "24/7 availability and competitive pricing"
  ];

  const events = [
    "Australian Open at Melbourne Park",
    "AFL matches at the MCG",
    "Melbourne Cup at Flemington",
    "Australian Grand Prix at Albert Park",
    "Melbourne International Comedy Festival",
    "Melbourne Fashion Week"
  ];

  const popularRoutes = [
    { from: "Melbourne Airport (MEL)", to: "Melbourne CBD", price: "From $105" },
    { from: "Melbourne Airport (MEL)", to: "Southbank", price: "From $120" },
    { from: "Avalon Airport (AVV)", to: "Melbourne CBD", price: "From $175" },
    { from: "Melbourne CBD", to: "Yarra Valley", price: "From $250" },
    { from: "Melbourne CBD", to: "Mornington Peninsula", price: "From $330" },
    { from: "Melbourne Airport (MEL)", to: "Geelong", price: "From $300" }
  ];

  const suburbs = [
    "Melbourne CBD", "Southbank", "South Yarra", "Toorak", "St Kilda",
    "Docklands", "Carlton", "Fitzroy", "Richmond", "Brighton",
    "Prahran", "Chapel Street", "Collins Street", "Hawthorn", "Kew"
  ];

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me Melbourne" className="h-12 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
              <Link to="/#fleet" className="text-muted-foreground hover:text-foreground transition-colors">Fleet</Link>
              <Link to="/#locations" className="text-muted-foreground hover:text-foreground transition-colors">Locations</Link>
              <Link to="/#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+61481437772" className="hidden sm:flex items-center gap-2 text-primary">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">+61 481 437 772</span>
              </a>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* SEO Helmet */}
      <Helmet>
        <title>Melbourne Limo Service | Airport Transfers & Corporate Chauffeur</title>
        <meta name="description" content="Melbourne luxury chauffeur services for airport transfers, sporting events, corporate travel and winery tours. Premium fleet available 24/7. Book now." />
        <meta name="keywords" content="Melbourne limo, Melbourne chauffeur, Melbourne airport transfer, corporate travel Melbourne, winery tours Melbourne, MEL airport limo" />
        <link rel="canonical" href="https://www.limonearme.com.au/chauffeur/melbourne" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1514395462725-fb4566210144?w=1920&h=1080&fit=crop')",
          }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 hero-glow" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">Melbourne, Victoria</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-6">
              Melbourne Limo Service<br />
              <span className="text-primary">& Airport Transfers</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Experience Melbourne's vibrant lifestyle with premium limousine and airport transfer services 
              tailored for every traveller. Arranging timeliness and comfort best describe our Melbourne limo service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold glow-primary"
                >
                  Get Melbourne Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg border-black/20 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Why Book Melbourne Limo Service?
              </h2>
              <p className="text-muted-foreground mb-6">
                At Limo Near Me, we deliver premium limo and chauffeur services across Melbourne. 
                From airport transfers to winery tours and special events, we've got you covered.
              </p>
              <ul className="space-y-4 mb-8">
                {whyChoose.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Book Melbourne Limo <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&h=400&fit=crop" 
                alt="Melbourne Airport Limo Transfer"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Plane className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Melbourne Airport</p>
                    <p className="text-muted-foreground text-sm">MEL & AVV Airports</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Luxury Limo Services in Melbourne</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive luxury limo and chauffeur solutions across Melbourne and Victoria
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card group hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Melbourne Events Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <img 
                src="https://images.unsplash.com/photo-1529470839332-78ad660a6a82?w=600&h=400&fit=crop" 
                alt="Melbourne Events"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Trophy className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Melbourne Events</p>
                    <p className="text-muted-foreground text-sm">Sports & Entertainment</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Explore Melbourne Your Way
              </h2>
              <p className="text-muted-foreground mb-6">
                Melbourne is Australia's sporting capital. Our chauffeurs provide seamless transport to major 
                events, avoiding traffic and parking hassles so you can enjoy the action.
              </p>
              <ul className="space-y-4 mb-8">
                {events.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Book Event Transfer <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Melbourne Limo Transfer Routes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Fixed-price limo transfers from Melbourne Airport and CBD
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card hover:border-primary/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-foreground font-medium">{route.from}</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-foreground font-medium">{route.to}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-black/10">
                  <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                  <Link to="/book">
                    <Button size="sm" variant="outline" className="rounded-full">
                      Book <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Melbourne Limo Service Areas</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Limo services across Melbourne and surrounding regions
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3"
          >
            {suburbs.map((suburb, index) => (
              <span 
                key={index}
                className="px-4 py-2 rounded-full bg-card border border-black/10 text-muted-foreground text-sm hover:border-primary/50 hover:text-primary transition-all cursor-default"
              >
                {suburb}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Book Your Melbourne Limo Today
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience luxury on every journey with Melbourne's premier limo service. Get an instant quote or call our 24/7 booking team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold glow-primary"
                >
                  Get a Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-10 py-6 text-lg border-primary/50 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> +61 481 437 772
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <SeoFooter />
    </div>
    </PageTransition>
  );
};

export default MelbournePage;
